import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaDownload, FaPrint, FaSave } from "react-icons/fa";
import { toast } from "react-toastify";
import Navbar from "./Navbar";
import axiosInstance from "../config/axios";
import { buildCsv, downloadCsvFile } from "../utils/csvDownload";
import logo from "../Assets/logo3.jpg";
import {
  CUSTOM_REPORT_ADDRESS_KEYS,
  CUSTOM_REPORT_COLUMNS,
  CUSTOM_REPORT_SECONDARY_FILTERS,
  applyCustomReportSecondaryFilters,
  buildCustomReportAgentOptions,
  buildCustomReportRows,
  closingDateIsoForInput,
  customReportRowToTradePatch,
  emptyCustomReportSecondaryFilters,
  formatCustomReportPeriodLabel,
  formatIsoToMMDDYYYY,
  formatMMDDYYYYToIso,
  getCustomReportSecondaryFilterOptions,
  propertyAddressDisplay,
} from "../utils/customReportUtils";

const COMPANY = "ABC Realty Inc., Brokerage";
const REPORT_TITLE = "Custom Report";

const cellInputClass =
  "w-full min-w-[4.5rem] px-1.5 py-1 text-xs border border-gray-200 rounded bg-white focus:ring-1 focus:ring-blue-600 focus:outline-none";
const filterSelectClass =
  "w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const CustomReport = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isFinancePage = pathname.startsWith("/finance-");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [secondaryFilters, setSecondaryFilters] = useState(
    emptyCustomReportSecondaryFilters,
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [allTrades, setAllTrades] = useState([]);

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const res = await axiosInstance.get("/agents");
        setAgents(res.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    loadAgents();
  }, []);

  const agentOptions = useMemo(
    () => buildCustomReportAgentOptions(agents, rows),
    [agents, rows],
  );
  const visibleRows = useMemo(
    () => applyCustomReportSecondaryFilters(rows, secondaryFilters),
    [rows, secondaryFilters],
  );

  const periodLabel = formatCustomReportPeriodLabel(fromDate, toDate);
  const dirtyCount = rows.filter((r) => r.dirty).length;

  const handleGenerate = async () => {
    if (!fromDate && !toDate) {
      toast.error("Select a from and/or to date for the period.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/trades");
      const trades = res.data || [];
      setAllTrades(trades);
      const built = buildCustomReportRows(trades, fromDate, toDate);
      setRows(built);
      setSecondaryFilters(emptyCustomReportSecondaryFilters());
      setShowReport(true);
      if (!built.length) {
        toast.info("No trades with a closing date in that period.");
      }
    } catch (err) {
      console.error(err);
      setError("Could not load trades for the custom report.");
      toast.error("Could not load trades.");
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (rowId, key, value) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.rowId !== rowId) return row;
        const next = { ...row, [key]: value, dirty: true };
        if (key === "closingDate") {
          const iso = formatMMDDYYYYToIso(value);
          next.closingDate = formatIsoToMMDDYYYY(iso || value) || value;
          next.closingDateIso = iso || row.closingDateIso;
        }
        if (key === "tradeNumber") {
          const match = allTrades.find(
            (t) =>
              String(t.tradeNumber || t.keyInfo?.tradeNumber) === String(value),
          );
          if (match) {
            const rebuilt = buildCustomReportRows(
              [match],
              fromDate || "1900-01-01",
              toDate || "2999-12-31",
            )[0];
            if (rebuilt) {
              return {
                ...rebuilt,
                rowId: row.rowId,
                dirty: true,
              };
            }
          }
        }
        return next;
      }),
    );
  };

  const handleSaveOverrides = async () => {
    const dirty = rows.filter((r) => r.dirty && r.tradeId);
    if (!dirty.length) {
      toast.info("No overrides to save.");
      return;
    }
    setSaving(true);
    try {
      for (const row of dirty) {
        const patch = customReportRowToTradePatch(row);
        await axiosInstance.put(`/trades/${row.tradeId}`, {
          keyInfo: patch.keyInfo,
        });
      }
      setRows((prev) => prev.map((r) => ({ ...r, dirty: false })));
      toast.success(`Saved overrides for ${dirty.length} trade(s).`);
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message || "Failed to save trade overrides.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadCsv = () => {
    const headers = CUSTOM_REPORT_COLUMNS.map((c) => c.label);
    const data = visibleRows.map((row) =>
      CUSTOM_REPORT_COLUMNS.map((col) => {
        if (col.key === "closingDate") {
          return row.closingDate || "";
        }
        return row[col.key] ?? "";
      }),
    );
    const csv = buildCsv(headers, data);
    downloadCsvFile(
      `custom-report_${fromDate || "start"}_${toDate || "end"}.csv`,
      csv,
    );
  };

  const handlePrint = () => {
    const bodyRows = visibleRows
      .map((row) => {
        const cells = CUSTOM_REPORT_COLUMNS.map((col) => {
          let val = row[col.key] ?? "";
          if (col.key === "closingDate") val = row.closingDate || "";
          return `<td>${escapeHtml(val)}</td>`;
        }).join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(REPORT_TITLE)}</title>
  <style>
    @page { size: landscape; margin: 8mm; }
    * { box-sizing: border-box; }
    html, body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 7px;
      color: #111;
      margin: 0;
      padding: 0;
      width: 100%;
    }
    .header {
      text-align: center;
      margin-bottom: 8px;
    }
    .header .logo {
      display: block;
      height: 40px;
      width: auto;
      margin: 0 auto 6px;
    }
    .company { font-size: 11px; font-weight: 700; }
    .title { font-size: 12px; font-weight: 700; margin-top: 2px; }
    .period { color: #444; margin-top: 2px; font-size: 8px; }
    .table-wrap {
      width: 100%;
      border: 1px solid #000;
      overflow: hidden;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      border: none;
    }
    th, td {
      border: 1px solid #000;
      padding: 2px 3px;
      text-align: left;
      vertical-align: top;
      word-wrap: break-word;
      overflow-wrap: anywhere;
      font-size: 7px;
      line-height: 1.2;
    }
    th {
      background: #f3f4f6;
      font-size: 6.5px;
      font-weight: 700;
    }
    @media print {
      html, body { margin: 0; padding: 0; }
      .table-wrap { border: 1px solid #000 !important; }
      th, td { border: 1px solid #000 !important; }
    }
  </style>
</head>
<body>
  <div class="header">
    <img class="logo" src="${logo}" alt="Logo" />
    <div class="company">${escapeHtml(COMPANY)}</div>
    <div class="title">${escapeHtml(REPORT_TITLE)}</div>
    <div class="period">${escapeHtml(periodLabel || "All selected dates")}</div>
  </div>
  <div class="table-wrap">
    <table>
      <thead>
        <tr>
          ${CUSTOM_REPORT_COLUMNS.map((c) => `<th>${escapeHtml(c.label)}</th>`).join("")}
        </tr>
      </thead>
      <tbody>
        ${bodyRows || `<tr><td colspan="${CUSTOM_REPORT_COLUMNS.length}">No rows</td></tr>`}
      </tbody>
    </table>
  </div>
</body>
</html>`;
    const w = window.open("", "_blank", "width=1200,height=900");
    if (!w) {
      toast.error("Pop-up blocked. Allow pop-ups to print.");
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
      try {
        w.print();
      } catch (_) {}
    }, 300);
  };

  const renderCell = (row, col) => {
    const value = row[col.key] ?? "";

    if (CUSTOM_REPORT_ADDRESS_KEYS.has(col.key) || col.edit === "text") {
      return (
        <input
          type="text"
          className={cellInputClass}
          value={value}
          onChange={(e) => updateRow(row.rowId, col.key, e.target.value)}
        />
      );
    }

    if (col.edit === "date" || col.key === "closingDate") {
      return (
        <input
          type="date"
          className={cellInputClass}
          value={closingDateIsoForInput(row.closingDate)}
          onChange={(e) => {
            const iso = e.target.value;
            updateRow(row.rowId, "closingDate", formatIsoToMMDDYYYY(iso));
          }}
          title={row.closingDate || ""}
        />
      );
    }

    // No column dropdowns — plain text (filter above the table instead)
    return <span className="block px-1.5 py-1 text-xs">{value || "—"}</span>;
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex-1 p-6">
        <div className="max-w-[96rem] mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Custom Report
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Select a closing-date period, then override row values before
                printing or saving.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                navigate(
                  isFinancePage ? "/finance" : "/financial-reports",
                )
              }
              className="text-sm text-blue-700 hover:underline"
            >
              ← Back to Reports
            </button>
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
            <h2 className="text-sm font-semibold text-gray-800 mb-3">
              Period selection
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  From (closing date)
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  To (closing date)
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full sm:w-auto px-4 py-2 bg-blue-900 text-white rounded text-sm font-medium hover:bg-blue-800 disabled:opacity-60"
                >
                  {loading ? "Generating…" : "Generate report"}
                </button>
              </div>
            </div>
            {error ? (
              <p className="text-sm text-red-600 mt-3">{error}</p>
            ) : null}
          </div>

          {showReport ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h2 className="text-sm font-semibold text-gray-800">
                  Secondary filters
                </h2>
                <button
                  type="button"
                  onClick={() =>
                    setSecondaryFilters(emptyCustomReportSecondaryFilters())
                  }
                  className="text-xs text-blue-700 hover:underline"
                >
                  Clear filters
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {CUSTOM_REPORT_SECONDARY_FILTERS.map((f) => {
                  const options = getCustomReportSecondaryFilterOptions(
                    f.key,
                    rows,
                    agentOptions,
                  );
                  return (
                    <div key={f.key}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {f.label}
                      </label>
                      <select
                        className={filterSelectClass}
                        value={secondaryFilters[f.key] || ""}
                        onChange={(e) =>
                          setSecondaryFilters((prev) => ({
                            ...prev,
                            [f.key]: e.target.value,
                          }))
                        }
                      >
                        {options.map((o) => (
                          <option key={`${f.key}-${o.value || "any"}`} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {showReport ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <div className="text-sm font-semibold text-gray-500">
                    {COMPANY}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {REPORT_TITLE}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {periodLabel || "Selected period"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {visibleRows.length} row(s)
                    {visibleRows.length !== rows.length
                      ? ` of ${rows.length}`
                      : ""}
                    {dirtyCount
                      ? ` · ${dirtyCount} unsaved override(s)`
                      : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleSaveOverrides}
                    disabled={saving || !dirtyCount}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FaSave /> {saving ? "Saving…" : "Save overrides"}
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadCsv}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    <FaDownload /> CSV
                  </button>
                  <button
                    type="button"
                    onClick={handlePrint}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded bg-blue-900 text-white hover:bg-blue-800"
                  >
                    <FaPrint /> Print
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded">
                <table className="min-w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-100 text-left">
                      {CUSTOM_REPORT_COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          className="border border-gray-200 px-2 py-2 font-semibold whitespace-nowrap"
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={CUSTOM_REPORT_COLUMNS.length}
                          className="border px-3 py-6 text-center text-gray-500"
                        >
                          No trades match the selected filters.
                        </td>
                      </tr>
                    ) : (
                      visibleRows.map((row) => (
                        <tr
                          key={row.rowId}
                          className={
                            row.dirty ? "bg-amber-50/60" : "bg-white"
                          }
                        >
                          {CUSTOM_REPORT_COLUMNS.map((col) => (
                            <td
                              key={`${row.rowId}-${col.key}`}
                              className="border border-gray-200 px-1 py-1 align-top"
                              title={
                                col.key === "streetName"
                                  ? propertyAddressDisplay(row)
                                  : undefined
                              }
                            >
                              {renderCell(row, col)}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Column dropdowns removed. Use the six secondary filters above to
                narrow rows. Address fields are text; closing date uses
                month/day/year (MM/DD/YYYY).
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CustomReport;
